import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { ChevronDown, LayoutDashboard, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { navigationModules, NAV_SECTIONS } from '../lib/navigation';
import type { NavModule } from '../lib/navigation';

interface SidebarProps {
  onNavigate?: () => void;
  restartTour?: () => void;
  collapsed?: boolean;
  /** Render the brand lockup at the top of the rail. Off when the shell shows
   * the brand in the full-width top bar (desktop); on for the mobile drawer. */
  showBrand?: boolean;
}

function getDataTourAttr(moduleId: string): string | undefined {
  if (moduleId === 'stores') return 'first-restaurant-link';
  if (moduleId === 'admin') return 'admin-section';
  if (moduleId === 'help') return 'help-section';
  return undefined;
}

// Shared row classes for a top-level nav item (direct link or accordion header).
function rowClasses(isActive: boolean, collapsed: boolean): string {
  return [
    'flex items-center rounded-lg text-sm font-medium transition-colors w-full',
    collapsed ? 'justify-center px-2 py-2' : 'gap-2.5 px-3 py-2',
    isActive
      ? 'bg-sidebar-active text-sidebar-text-active'
      : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
  ].join(' ');
}

function NavSection({
  module,
  onNavigate,
  collapsed,
}: {
  module: NavModule;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const location = useLocation();
  const { can } = useAuth();
  const { environment } = useEnvironment();
  const hasActiveChild = module.children.some((item) => location.pathname.startsWith(item.path));
  const [expanded, setExpanded] = useState(hasActiveChild);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Collapsed rail: anchor the flyout with fixed positioning so the sidebar's
  // own overflow-y-auto can't clip it.
  function toggleFlyout() {
    if (flyoutOpen) {
      setFlyoutOpen(false);
      return;
    }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setFlyoutPos({ top: rect.top, left: rect.right + 6 });
    setFlyoutOpen(true);
  }

  const visibleChildren = module.children.filter(
    (item) => !item.capability || can(environment, item.capability.domain, item.capability.action),
  );

  if (visibleChildren.length === 0) return null;

  // Active module icon glows amber on the cosmos rail (design: .nav__item.active .nav__icon).
  const iconColor = hasActiveChild ? 'text-brand-amber' : 'text-sidebar-text-muted';
  const tourAttr = getDataTourAttr(module.id);

  // Single-destination module → render as a direct link (sidebar is module-level).
  if (visibleChildren.length === 1) {
    const item = visibleChildren[0];
    const isActive =
      location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        title={module.label}
        {...(tourAttr ? { 'data-tour': tourAttr } : {})}
        className={rowClasses(isActive, !!collapsed)}
      >
        <module.icon
          className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand-amber' : 'text-sidebar-text-muted'}`}
        />
        {!collapsed && <span className="flex-1">{module.label}</span>}
        {!collapsed && module.adminOnly && <Lock className="h-3 w-3 text-sidebar-text-muted" />}
      </Link>
    );
  }

  if (collapsed) {
    return (
      <div className="relative" {...(tourAttr ? { 'data-tour': tourAttr } : {})}>
        <button
          ref={btnRef}
          type="button"
          title={module.label}
          onClick={toggleFlyout}
          className={rowClasses(hasActiveChild, true)}
        >
          <module.icon className={`h-4 w-4 ${iconColor}`} />
        </button>
        {flyoutOpen &&
          flyoutPos &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setFlyoutOpen(false)}
                aria-hidden="true"
              />
              <div
                className="fixed z-[61] min-w-[10rem] rounded-xl border border-white/10 bg-sidebar p-1 shadow-float"
                style={{ top: flyoutPos.top, left: flyoutPos.left }}
              >
                <p className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-sidebar-text-muted">
                  {module.label}
                </p>
                {visibleChildren.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={() => {
                        setFlyoutOpen(false);
                        onNavigate?.();
                      }}
                      className={`block rounded-lg px-3 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-sidebar-active text-sidebar-text-active font-medium'
                          : 'text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </>,
            document.body,
          )}
      </div>
    );
  }

  return (
    <div {...(tourAttr ? { 'data-tour': tourAttr } : {})}>
      <button onClick={() => setExpanded(!expanded)} className={rowClasses(hasActiveChild, false)}>
        <module.icon className={`h-4 w-4 shrink-0 ${iconColor}`} />
        <span className="flex flex-1 items-center gap-2">
          {module.label}
          {module.adminOnly && <Lock className="h-3 w-3 text-sidebar-text-muted" />}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-sidebar-text-muted transition-transform ${expanded ? '' : '-rotate-90'}`}
        />
      </button>
      {expanded && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
          {visibleChildren.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={onNavigate}
                className={`block rounded-lg px-3 py-1.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-sidebar-active text-sidebar-text-active font-medium'
                    : 'text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  onNavigate,
  restartTour: _restartTour,
  collapsed = false,
  showBrand = true,
}: SidebarProps) {
  const location = useLocation();
  const { can } = useAuth();
  const { environment } = useEnvironment();

  const visibleModules = navigationModules.filter(
    (module) =>
      (!module.envs || module.envs.includes(environment)) &&
      can(environment, module.capability.domain, module.capability.action),
  );

  const dashboardActive = location.pathname === '/';

  // Group visible modules into the configured sections, preserving section order.
  const grouped = NAV_SECTIONS.map((section) => ({
    section,
    modules: section.moduleIds
      .map((id) => visibleModules.find((m) => m.id === id))
      .filter((m): m is NavModule => Boolean(m)),
  })).filter((g) => g.modules.length > 0);

  return (
    <nav className={`flex flex-col gap-0.5 py-4 ${collapsed ? 'px-1 pb-12' : 'px-3'}`}>
      {/* Brand lockup — Byte mark over product label, on the dark cosmos rail.
          Hidden when the shell renders the brand in the full-width top bar. */}
      {showBrand && (
        <div className={`mb-3 ${collapsed ? 'flex justify-center px-1' : 'px-2'} pb-4 pt-1`}>
          <img
            src="/byte-mark-white.png"
            alt="Byte by Yum!"
            className={collapsed ? 'h-4 w-auto max-w-full' : 'h-6 w-auto'}
          />
          {!collapsed && (
            <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
              Coach CMDB
            </div>
          )}
        </div>
      )}

      <Link
        to="/"
        onClick={onNavigate}
        title="Dashboard"
        className={`${rowClasses(dashboardActive, collapsed)} mb-1`}
      >
        <LayoutDashboard
          className={`h-4 w-4 shrink-0 ${dashboardActive ? 'text-brand-amber' : 'text-sidebar-text-muted'}`}
        />
        {!collapsed && <span className="flex-1">Dashboard</span>}
      </Link>

      {grouped.map(({ section, modules }) => (
        <div key={section.id} className="mt-2">
          {!collapsed && (
            <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-sidebar-text-muted">
              {section.label}
            </div>
          )}
          <div className="space-y-0.5">
            {modules.map((module) => (
              <NavSection
                key={module.id}
                module={module}
                onNavigate={onNavigate}
                collapsed={collapsed}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
